import { LinkProps, LinkWrapper } from '../LinkWrapper';
import { Skeleton } from '../Skeleton';
import { RewardStep1 } from '../SVGIcons/RewardStep1';
import { Poster } from '../Poster';
import styles from './rwbanner.module.sass';
import cn from 'classnames';

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    isLoading: true;
    image?: string; // Optional when loading
    heading?: string | React.ReactElement;
    description?: string;
    cta?: LinkProps;
    onCtaClick?: (e: React.MouseEvent) => void;
    badge: React.ReactElement;
    isClickable?: false;
    onlyImage?: boolean;
}

interface LoadedProps extends BaseProps {
    isLoading?: false;
    image: string; // Required when not loading
    heading?: string | React.ReactElement;
    description?: string;
    cta?: LinkProps;
    onCtaClick?: (e: React.MouseEvent) => void; // Optional click handler for CTA
    badge?: React.ReactElement;
    isClickable?: boolean;
    onlyImage?: boolean;
}

type RewardBannerProps = LoadingProps | LoadedProps;

export const RewardBanner = ({
    image,
    heading,
    description,
    cta,
    onCtaClick,
    isLoading,
    badge,
    isClickable = false,
    onlyImage = false,
    ...props
}: RewardBannerProps) => {
    if (isLoading) {
        return (
            <div data-test-id="reward-banner-skeleton" {...props} className={cn(styles.rwbanner, props.className)}>
                <Skeleton absolute />
            </div>
        );
    }
    return (
        <div
            data-test-id="reward-banner"
            {...props}
            className={cn(styles.rwbanner, onlyImage && styles.static, props.className)}
            style={{
                ...(onlyImage ? {} : { backgroundImage: `url("${image}")` }),
                cursor: isClickable ? 'pointer' : 'default',
            }}
        >
            <div className={styles.coin}>{badge}</div>
            <div className={styles.content}>
                {typeof heading === 'string' ? <strong className={styles.heading}>{heading}</strong> : heading}
                {description && <span className={styles.description}>{description}</span>}
                <LinkWrapper
                    link={cta}
                    onClick={onCtaClick}
                    className={styles.link}
                    data-test-id="reward-banner-link"
                />
            </div>
            {onlyImage && (
                <Poster picture={image} inline />
            )}
        </div>
    );
};
